import './frame-file-picker';
import './ui-status';
import { isConversionCancelledMessage } from './conversion-cancel';

interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface TelegramWebApp {
  initData: string;
  platform?: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  ready(): void;
  expand(): void;
  close(): void;
  showAlert(message: string): void;
  showConfirm(message: string, callback: (confirmed: boolean) => void): void;
  openLink?: (url: string) => void;
  disableVerticalSwipes?: () => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

let telegram: TelegramWebApp | undefined;
const BOT_LAUNCH_STORAGE_KEY = 'krug_bot_launch';

function validLaunchToken(value: string | null): value is string {
  return Boolean(value && value.length <= 4_096 && /^[a-zA-Z0-9_.-]+$/u.test(value));
}

function loadBotLaunchToken(): string {
  const url = new URL(window.location.href);
  const pathMatch = url.pathname.match(/^(.*)\/launch\/([^/]+)\/?$/u);
  const fromPath = pathMatch?.[2] ?? null;
  const fromUrl = url.searchParams.get('launch') ?? fromPath;
  if (validLaunchToken(fromUrl)) {
    try {
      window.sessionStorage.setItem(BOT_LAUNCH_STORAGE_KEY, fromUrl);
    } catch {
      // The current page can still use the token when session storage is unavailable.
    }
    url.searchParams.delete('launch');
    const cleanPath = pathMatch?.[1] || '/';
    try {
      window.history.replaceState(null, '', `${cleanPath}${url.search}${url.hash}`);
    } catch {
      // Authorization must keep working even if an embedded browser blocks History API.
    }
    return fromUrl;
  }
  try {
    const stored = window.sessionStorage.getItem(BOT_LAUNCH_STORAGE_KEY);
    return validLaunchToken(stored) ? stored : '';
  } catch {
    return '';
  }
}

const botLaunchToken = loadBotLaunchToken();

function loadTelegramSdk(timeoutMs = 12_000): Promise<void> {
  if (window.Telegram?.WebApp) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const script = document.createElement('script');
    const finish = (): void => {
      window.clearTimeout(timeout);
      resolve();
    };
    const timeout = window.setTimeout(finish, timeoutMs);
    script.src = '/telegram/sdk.js';
    script.async = true;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', finish, { once: true });
    document.head.append(script);
  });
}

export function hasTelegramAuthorization(): boolean {
  return Boolean(telegram?.initData || botLaunchToken);
}

export function telegramPlatform(): string {
  return telegram?.platform?.trim() || 'unknown';
}

export async function initializeTelegram(): Promise<void> {
  await loadTelegramSdk();
  // The SDK also creates WebApp in ordinary browsers, but native dialogs there
  // have no Telegram host to answer them. Use the browser controls in that case.
  const candidate = window.Telegram?.WebApp;
  telegram = candidate?.initData ? candidate : undefined;
  telegram?.ready();
  telegram?.expand();
  telegram?.disableVerticalSwipes?.();
  document.documentElement.dataset.theme = telegram?.colorScheme ?? 'dark';
}

export function authHeaders(): HeadersInit {
  if (telegram?.initData) {
    return { Authorization: `tma ${telegram.initData}` };
  }
  if (botLaunchToken) {
    return { Authorization: `krug ${botLaunchToken}` };
  }
  return {};
}

export function alertUser(message: string): void {
  if (isConversionCancelledMessage(message)) {
    return;
  }
  if (telegram) {
    telegram.showAlert(message);
  } else {
    window.alert(message);
  }
}

export function confirmUser(message: string): Promise<boolean> {
  if (!telegram) {
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => telegram?.showConfirm(message, resolve));
}

export function openExternalLink(url: string): void {
  if (telegram?.openLink) {
    telegram.openLink(url);
    return;
  }
  window.location.assign(url);
}

void import('./billing');
void import('./conversion-controls');
void import('./owner-plan-switcher');
